import { relations } from 'drizzle-orm';
import { jsonb, pgTable, text, timestamp, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const users = pgTable('users', {
  id: id(),
  email: varchar('email', { length: 320 }).notNull().unique(),
  displayName: varchar('display_name', { length: 200 }),
  recoveryEmail: varchar('recovery_email', { length: 320 }),
  /** D-2 demo/test-data flag (docs/BUILD-PLAN-2026-07-04.md, Reserved call 1):
   *  the COARSE, per-user half of the two-level flag. 'real' | 'demo' | 'qa' —
   *  varchar per this codebase's house convention (jobs.source, watchlistSources.kind,
   *  companyTargets.status all take a plain varchar over a DB enum so a future
   *  value is a data value, not a migration), NOT a Postgres enum. Every
   *  pre-existing row defaults 'real' — the only honest value for a user who
   *  existed before this column did (NO INVENTION: nothing here backfills a
   *  demo/qa classification onto a real person). 'qa' is kept DISTINCT from
   *  'demo' (Reserved call 1 §4 decision 3) so an internal QA pass and a
   *  sales/onboarding demo account can be told apart later without a second
   *  migration, even though today's visibleTo() wall (lib/testDataWall.ts)
   *  treats both identically (both see everything, is_test_data included). */
  accountKind: varchar('account_kind', { length: 8 }).notNull().default('real'),
  preferences: jsonb('preferences').$type<{
    timezone: string;
    locale: string;
    notificationCadence: 'realtime' | 'daily' | 'weekly';
    voiceCorrectionsEnabled: boolean;
    autoTighten: boolean;
  }>(),
  /** D-2 deletion-REQUEST flow (docs/briefs/launch-week-briefs.md §D(c)). NULL =
   *  no pending request (the only state every pre-existing row can honestly be
   *  in — NO INVENTION forbids backfilling anything else). Set by POST
   *  /api/account/delete-request; cleared by POST /api/account/delete-cancel.
   *  This column ONLY records the request — it does not itself delete or purge
   *  anything (the nothing-unrecoverable law); the actual purge job that acts on
   *  a 30-day-elapsed request is a separate, later, out-of-scope lane (see the
   *  account.routes.ts header for the honest "scheduled, not yet built" note). */
  deletionRequestedAt: timestamp('deletion_requested_at', { withTimezone: true }),
  /** DATA-RESIDENCY Phase 0 (docs/design/data-residency-plan-2026-07-05.md,
   *  "Detect → confirm"). The CONFIRMED region decision — the only column
   *  the region-aware db factory is ever allowed to route real placement on.
   *  NULL = unset (every pre-existing row, honestly — NO INVENTION forbids
   *  backfilling a guess). varchar (not a DB enum) per house convention
   *  (accountKind, jobs.source, watchlistSources.kind) so a future region is
   *  a data value, not a migration. Written ONLY by POST
   *  /api/account/data-region/confirm — never by the detect step, never by
   *  any other write path. A suggestion (see suggestedDataRegion) is never
   *  promoted to this column automatically. */
  dataRegion: varchar('data_region', { length: 8 }),
  /** Cloudflare's SUGGESTED region from request.cf.country at OAuth
   *  find-or-create time (pickSuggestedRegion). Pending confirmation — kept
   *  even after confirmation as an honest record of what was suggested vs.
   *  what the user actually chose (they may differ, an override). NULL when
   *  cf.country was absent (local dev) or unmapped — never a guessed value. */
  suggestedDataRegion: varchar('suggested_data_region', { length: 8 }),
  /** Set once, the moment the confirm route runs. NULL means "no confirmed
   *  region yet" (unset or suggested-only state) — see the plan doc's
   *  unset/suggested/confirmed state table. */
  dataRegionConfirmedAt: timestamp('data_region_confirmed_at', { withTimezone: true }),
  /** TEMPLATES-P2 (docs/design/resume-template-library-2026-07-05.md,
   *  Decision 9: "column" over a per-locale row — a single global default).
   *  The user's chosen résumé template family, e.g. 'ats-plain' /
   *  'modern-professional'. NULL = no preference set yet (every pre-existing
   *  row, honestly — NO INVENTION forbids backfilling a guessed favourite);
   *  every export path falls back to the family's own documented default
   *  ('ats-plain') when this is null, never to a fabricated "last used"
   *  value. varchar (not a DB enum), matching resumeTemplates.family's own
   *  house-convention typing (a 15th family is a data value, not a
   *  migration) — validated against the closed ResumeTemplateFamily set in
   *  application code, not at the column level. Written ONLY by PATCH
   *  /api/account/template-preference. */
  preferredTemplateFamily: varchar('preferred_template_family', { length: 40 }),
  /** TEMPLATES-P2 §10 (Decision 12): the user's chosen résumé accent colour,
   *  stored as the OPTION KEY (e.g. 'sage-teal' / 'ink-navy'), resolved to its
   *  hex at render time — never the hex itself, so a future palette
   *  adjustment (a slightly different Burgundy) is a constant-table edit, not
   *  a backfill. NULL = unset, falls back to the brand default (Sage Teal) —
   *  the same "unset means the documented default, never a guess" posture as
   *  preferredTemplateFamily above. Written ONLY by the same PATCH
   *  /api/account/template-preference (one endpoint, two sibling
   *  preferences, mirroring how data-region's confirm route owns its one
   *  column). */
  preferredAccent: varchar('preferred_accent', { length: 24 }),
  /** D4 (docs/briefs/morning-2026-07-07/03-wiring-gaps.md decision #6) — the
   *  lifecycle-email digest's per-user IANA zone, INHERITED once at OAuth
   *  find-or-create time from Cloudflare's edge geo (`request.cf.timezone`),
   *  mirroring `suggestedDataRegion`'s own "detect once, never re-guess"
   *  posture exactly. NULL = cf.timezone was absent (local dev, or an
   *  edge that didn't report one) — NO INVENTION forbids backfilling a
   *  guess, so the digest sender falls back to the US-default fire window
   *  on null, never assumes AU. Jason's exact ruling: "default USA BUT IF
   *  someone is in AUS timezone on enrollment it needs to inherit AUS
   *  sydney - EU/UK = UK/London" — see mapTimezoneToDigestZone
   *  (apps/api/lib/db.ts) for the mapping. Written ONLY at find-or-create;
   *  never overwritten on subsequent sign-ins (the region column's own
   *  precedent — a later trip through a different geo does not silently
   *  relocate an existing user's digest window). */
  digestTimezone: varchar('digest_timezone', { length: 64 }),
  ...timestamps,
});

export const userCredentials = pgTable('user_credentials', {
  id: id(),
  userId: text('user_id').notNull(),
  type: varchar('type', { length: 32 }).notNull(),
  publicKey: text('public_key'),
  passwordHash: text('password_hash'),
  metadata: jsonb('metadata'),
  ...timestamps,
});

export const sessions = pgTable('sessions', {
  id: varchar('id', { length: 128 }).primaryKey(),
  userId: text('user_id').notNull(),
  expiresAt: timestamp('expires_at', { withTimezone: true }).notNull(),
  metadata: jsonb('metadata'),
  ...timestamps,
});

export const usersRelations = relations(users, ({ many }) => ({
  credentials: many(userCredentials),
  sessions: many(sessions),
}));
